// Right-click and "⋯" menus, for a widget or for empty canvas, in the one order every menu keeps (menus/MenuList.tsx).

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { create } from "zustand";
import { ArrowDownToLine, ArrowUpRight, ArrowUpToLine, BriefcaseBusiness, ClipboardPaste, Code2, Copy, CopyPlus, Heading, Lock, PencilRuler, Maximize, Maximize2, MessageCircle, MessageSquareText, Plug, Plus, RefreshCw, SlidersHorizontal, StickyNote, Trash2, TriangleAlert, Unlock, Wand2 } from "lucide-react";
import type { ID, WidgetItem } from "../../shared/types";
import { SPEC } from "../../shared/catalog";
import { addWidget, archivedOn, bringToFront, copyItems, deleteItems, duplicateItems, pasteItems, sendToBack, switchBoard, toggleLock } from "../state/actions";
import { get, set, toast } from "../state/store";
import { boardActions } from "./menus/BoardActions";
import { runPull } from "../state/sources";
import { camera } from "../canvas/camera";
import { bounds, itemRect } from "../canvas/geometry";
import { withMap } from "../boards/mindmap/live";
import { boardItems } from "../state/store";
import { copyText, isMac } from "../lib/util";
import { htmlHash, problems } from "../../shared/widgetKit";
import { fixIt, fixLabel } from "../widgets/custom";
import { canFork, makeOwn } from "../widgets/fork";
import { giveJob, jobOf, jobOffered, openJob } from "../widgets/job";
import { askLabel, builderNow, handOff, askWho } from "../agent/handoff";
import { buttonAsk, changeAsk, drawAsk } from "../agent/asks";
import { enterFullscreen } from "./Fullscreen";
import { hasPages, kindOf } from "../../shared/boardKinds";
import { shelfMove } from "../boards/pipeline/place";
import { removeFromPage } from "../boards/doc/place";
import { MenuList, type MenuEntry } from "./menus/MenuList";
import { askName } from "../../shared/askWords";

const MOD = isMac ? "⌘" : "Ctrl+";

interface MenuState {
  open: boolean;
  x: number;
  y: number;
  /** board: a board in the boards list or All boards, ids holding its id */
  kind: "item" | "canvas" | "board";
  ids: ID[];
  at: { x: number; y: number } | null;
  align?: "left" | "right";
}

export const useMenu = create<MenuState>(() => ({ open: false, x: 0, y: 0, kind: "canvas", ids: [], at: null }));

export function openContextMenu(m: Omit<MenuState, "open">) {
  useMenu.setState({ ...m, open: true });
}

/** A board's menu shut by a press outside it, and where: the click that press becomes, on that board's open tab, leaves
 * it shut rather than opening it again. */
let shut: { id: string; at: number; on: EventTarget | null } = { id: "", at: 0, on: null };

export function closeContextMenu() {
  useMenu.setState({ open: false });
}

/** Whether this board's menu was shut a moment ago by a press on this element, the press now becoming a click. */
export const menuJustShut = (id: string, el: Element) => shut.id === id && Date.now() - shut.at < 600 && shut.on instanceof Node && el.contains(shut.on);

type Entry = MenuEntry;

/** A board's own menu, from a right-click on it in the boards list or All boards (Features 1, 20 Oct). */
function boardEntries(id: ID): Entry[] {
  const s = get();
  const b = s.boards.find((x) => x.id === id) ?? s.archived.find((x) => x.id === id);
  if (!b) return [];
  const here = s.boardId === id;
  const open: Entry = { label: "Open", group: "main", icon: <ArrowUpRight size={14} />, run: () => (switchBoard(id), set({ panel: s.panel === "boards" ? null : s.panel })) };
  // the board's whole menu, as its ⋯ has it (David, 1 Nov: a tab's right-click had only a few of its rows); on another
  // board, Open first, and what opens on a board (its look, Share, its instructions) opens it there
  return [...(here ? [] : [open]), ...boardActions(b, s.boards.length, here)];
}

function boardHead(id: ID): { title: string; sub: string | null } | null {
  const s = get();
  const b = s.boards.find((x) => x.id === id) ?? s.archived.find((x) => x.id === id);
  return b ? { title: b.name, sub: b.archived ? `Archived on ${archivedOn(b.archived)}` : null } : null;
}

export function agentPrompt(w: WidgetItem, question = ""): string {
  return `On my Context Board, look at the "${w.title}" widget (id ${w.id}${w.stream ? `, stream ${w.stream}` : ""}). Read it with read_board first.${question ? " " + question : ""}`;
}

function itemEntries(ids: ID[]): Entry[] {
  const s = get();
  const items = ids.map((id) => s.items[id]).filter(Boolean);
  const single = items.length === 1 ? items[0] : null;
  const locked = items.every((i) => i.locked);
  // a widget on a typed board: in a page on a doc board, on the shelf beside a calendar's month (a mind map's
  // widgets float on its canvas, so theirs is the canvas's menu)
  const typed = !!single && !["canvas", "mindmap"].includes(kindOf(s.boards.find((b) => b.id === single.boardId)));
  const inPage = typed && hasPages(kindOf(s.boards.find((b) => b.id === single!.boardId)));
  const out: Entry[] = [];
  if (single?.kind === "widget") {
    const w = single;
    const spec = SPEC[w.type];
    out.push({ label: "Settings", group: "main", icon: <SlidersHorizontal size={14} />, run: () => set({ selection: [w.id], inspect: true, editing: null }) });
    if (spec.reads && w.type !== "agent" && !(w.type === "feed" && w.props.source !== "stream")) {
      out.push({ label: "Data and source", group: "data", icon: <Plug size={14} />, run: () => set({ panel: "data", panelArg: { stream: w.stream ?? null, itemId: w.id } }) });
      const pullId = w.stream ? s.streams[w.stream]?.pullId : null;
      if (pullId)
        out.push({
          label: "Refresh now",
          group: "data",
          icon: <RefreshCw size={14} />,
          run: () => {
            void runPull(pullId).then(
              (r) => toast({ level: r.pull.status === "ok" ? "success" : "warn", title: r.pull.status === "ok" ? "Refreshed" : "Could not refresh", body: r.pull.error }),
              (e) => toast({ level: "error", title: "Could not refresh", body: (e as Error).message }),
            );
          },
        });
    }
    if (w.type === "custom") {
      // a widget an agent wrote is changed the same way: by asking
      const html = String(w.props.html ?? "");
      const r = s.renders[w.id];
      const report = r && html && r.hash === htmlHash(html) ? r.report : null;
      const b = builderNow();
      const who = askWho(b);
      if (report && problems(report).length) out.push({ label: fixLabel(b), group: "ask", icon: <TriangleAlert size={14} />, run: () => fixIt(w, report) });
      out.push({
        label: html ? `Change it with ${who}` : `Describe it to ${who}`,
        group: "ask",
        icon: <Wand2 size={14} />,
        run: () => {
          set({ selection: [w.id] });
          handOff(html ? changeAsk(w) : drawAsk(w));
        },
      });
      if (html) out.push({ label: "Edit the code", group: "more", icon: <Code2 size={14} />, run: () => set({ selection: [w.id], codeFor: w.id }) });
    } else if (w.type === "action") {
      // a button changes by asking too (Round 7): what it does, or its words; the owner's alone, as its settings are
      if (!s.guest)
        out.push({
          label: `Change it with ${askWho(builderNow())}`,
          group: "ask",
          icon: <Wand2 size={14} />,
          run: () => {
            set({ selection: [w.id] });
            handOff(buttonAsk(w));
          },
        });
    } else
      out.push({
        label: `${askLabel(builderNow())} about this`,
        group: "ask",
        icon: <MessageCircle size={14} />,
        run: () => {
          set({ selection: [w.id] });
          handOff({ chat: "What does this tell me, and what should I do about it?", agent: `On my Context Board, what does the widget “${askName(w.title) ?? w.type}” (id ${w.id}) tell me, and what should I do about it?` });
        },
      });
    // a job that fills it, on the user's own computer
    if (jobOf(w)) out.push({ label: "Its job", group: "data", icon: <BriefcaseBusiness size={14} />, run: () => openJob(w.id) });
    else if (jobOffered(w)) out.push({ label: "Give it a job", group: "data", icon: <BriefcaseBusiness size={14} />, run: () => giveJob(w) });
    // a built-in made the user's own, in place, to change freely
    if (canFork(w)) out.push({ label: "Make it my own", group: "more", icon: <PencilRuler size={14} />, run: () => void makeOwn(w.id) });
    if (s.keys.some((k) => k.kind === "agent"))
      out.push({
        label: "Copy a prompt for your agent",
        group: "more",
        icon: <MessageSquareText size={14} />,
        run: () => {
          void copyText(agentPrompt(w, "What does it tell you, and what should I do about it?"));
          toast({ level: "success", title: "Prompt copied", body: "Paste it into an agent connected to this board. It will read this widget." });
        },
      });
    // a Button has nothing to ask about or to see full screen: it is pressed
    if (!typed && w.type !== "action") {
      out.push({
        label: "Full screen",
        group: "edit",
        icon: <Maximize2 size={14} />,
        run: () => {
          set({ selection: [w.id] });
          void enterFullscreen({ solo: true });
        },
      });
    }
  }
  // a widget in a page: its block is where it is, so the canvas's arranging is not for it, and Delete takes the block too
  if (single && inPage) {
    out.push({ label: "Delete", group: "delete", icon: <Trash2 size={14} />, danger: true, run: () => removeFromPage(single.id) });
    return out;
  }
  // a widget on a pipeline's shelf: its place is up or down the shelf, and the canvas's arranging is not for it
  if (single && kindOf(s.boards.find((b) => b.id === single.boardId)) === "pipeline") {
    out.push({ label: "Move up the shelf", group: "edit", icon: <ArrowUpToLine size={14} />, disabled: !shelfMove(single.id, -1, true), run: () => shelfMove(single.id, -1) });
    out.push({ label: "Move down the shelf", group: "edit", icon: <ArrowDownToLine size={14} />, disabled: !shelfMove(single.id, 1, true), run: () => shelfMove(single.id, 1) });
    out.push({ label: "Delete", group: "delete", icon: <Trash2 size={14} />, danger: true, disabled: locked, run: () => deleteItems(ids) });
    return out;
  }
  // on a shelf the widget's place is its turn in the list: the canvas's arranging is not for it
  if (single && typed) {
    out.push({ label: "Delete", group: "delete", icon: <Trash2 size={14} />, danger: true, disabled: locked, run: () => deleteItems(ids) });
    return out;
  }
  out.push({ label: "Duplicate", group: "edit", icon: <CopyPlus size={14} />, kbd: `${MOD}D`, run: () => duplicateItems(ids) });
  out.push({ label: "Copy", group: "edit", icon: <Copy size={14} />, kbd: `${MOD}C`, run: () => copyItems(ids) });
  out.push({ label: "Bring to front", group: "more", icon: <ArrowUpToLine size={14} />, kbd: "]", run: () => bringToFront(ids) });
  out.push({ label: "Send to back", group: "more", icon: <ArrowDownToLine size={14} />, kbd: "[", run: () => sendToBack(ids) });
  out.push({ label: locked ? "Unlock" : "Lock in place", group: "more", icon: locked ? <Unlock size={14} /> : <Lock size={14} />, kbd: "⇧L", run: () => toggleLock(ids) });
  out.push({ label: ids.length > 1 ? `Delete ${ids.length}` : "Delete", group: "delete", icon: <Trash2 size={14} />, kbd: "⌫", danger: true, disabled: locked, run: () => deleteItems(ids) });
  return out;
}

function canvasEntries(at: { x: number; y: number } | null): Entry[] {
  return [
    { label: "Add a widget here", group: "main", icon: <Plus size={14} />, kbd: "A", run: () => set({ panel: "gallery", panelArg: { at } }) },
    { label: "Sticky note", group: "main", icon: <StickyNote size={14} />, kbd: "N", run: () => addWidget("sticky", { at: at ? { x: at.x - 112, y: at.y - 112 } : undefined }) },
    { label: "Heading", group: "main", icon: <Heading size={14} />, run: () => addWidget("heading", { at: at ? { x: at.x - 180, y: at.y - 36 } : undefined, props: { text: "New section", align: "left" } }) },
    { label: "Paste", group: "edit", icon: <ClipboardPaste size={14} />, kbd: `${MOD}V`, run: () => pasteItems() },
    {
      // one name for it everywhere: the board menu, the zoom island and the command bar say See everything too
      label: "See everything",
      group: "edit",
      icon: <Maximize size={14} />,
      kbd: "⇧1",
      run: () => camera.fit(bounds(withMap(boardItems(get()).map(itemRect))), { maxZ: 1 }),
    },
  ];
}

/** What a widget's menu is about, named at its head: the widget and its kind, or how many. */
function menuHead(ids: ID[]): { title: string; sub: string | null } | null {
  const items = ids.map((id) => get().items[id]).filter(Boolean);
  if (items.length > 1) return { title: `${items.length} selected`, sub: null };
  const it = items[0];
  if (!it) return null;
  if (it.kind !== "widget") return { title: it.kind === "ink" ? "Drawing" : "Item", sub: null };
  const label = SPEC[it.type].label;
  const title = it.title?.trim() || label;
  return { title, sub: title === label ? null : label };
}

/** Where the widgets a menu is about are on the screen, together, or null when none is drawn. */
function onScreen(ids: ID[]): { left: number; top: number; right: number; bottom: number } | null {
  let box: { left: number; top: number; right: number; bottom: number } | null = null;
  for (const id of ids) {
    const el = document.querySelector<HTMLElement>(`[data-item-id="${CSS.escape(id)}"]`);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    box = box ? { left: Math.min(box.left, r.left), top: Math.min(box.top, r.top), right: Math.max(box.right, r.right), bottom: Math.max(box.bottom, r.bottom) } : { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
  }
  return box;
}

export function ContextMenu() {
  const m = useMenu();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const entries = m.open ? (m.kind === "item" ? itemEntries(m.ids) : m.kind === "board" ? boardEntries(m.ids[0]) : canvasEntries(m.at)) : [];
  useLayoutEffect(() => {
    if (!m.open || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const W = window.innerWidth;
    const H = window.innerHeight;
    const gap = 10;
    // a widget's menu opens beside it, right or left, so the widget it is about stays in view
    const tile = m.kind === "item" ? onScreen(m.ids) : null;
    let x: number;
    let y: number;
    if (tile && tile.right + gap + r.width <= W - 8) x = tile.right + gap;
    else if (tile && tile.left - gap - r.width >= 8) x = tile.left - gap - r.width;
    else x = m.align === "right" ? m.x - r.width : m.x;
    // level with where it was opened, kept on the screen
    if (tile && (x >= tile.right || x + r.width <= tile.left)) y = Math.min(Math.max(8, Math.min(m.y, tile.bottom) - 18), H - r.height - 8);
    else {
      y = m.y;
      if (y + r.height > H - 8) y = Math.max(8, m.y - r.height);
    }
    if (x + r.width > W - 8) x = W - r.width - 8;
    setPos({ x: Math.max(8, x), y: Math.max(8, y) });
  }, [m.open, m.x, m.y, m.align, m.kind, m.ids]);
  useEffect(() => {
    if (!m.open) return;
    const close = (e: PointerEvent) => {
      if (ref.current?.contains(e.target as Node)) return;
      const now = useMenu.getState();
      if (now.kind === "board") shut = { id: now.ids[0] ?? "", at: Date.now(), on: e.target };
      closeContextMenu();
    };
    window.addEventListener("pointerdown", close, true);
    window.addEventListener("resize", closeContextMenu);
    return () => {
      window.removeEventListener("pointerdown", close, true);
      window.removeEventListener("resize", closeContextMenu);
    };
  }, [m.open]);
  // More opening near the foot of the screen: the menu moves up so its rows stay in sight
  useLayoutEffect(() => {
    const el = ref.current;
    if (!m.open || !el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      if (r.bottom > window.innerHeight - 8) setPos((p) => ({ ...p, y: Math.max(8, window.innerHeight - 8 - r.height) }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [m.open]);
  if (!m.open) return null;
  const head = m.kind === "item" ? menuHead(m.ids) : m.kind === "board" ? boardHead(m.ids[0]) : null;
  return (
    <div ref={ref} className={m.kind === "board" ? "menu island board-ctx-menu" : "menu island"} style={{ left: pos.x, top: pos.y }} role="menu" aria-label={head ? head.title : "Board"}>
      {head && (
        <div className="menu-head">
          <b>{head.title}</b>
          {head.sub && <span>{head.sub}</span>}
        </div>
      )}
      {/* a new key each time it opens, so More starts shut */}
      <MenuList key={`${m.kind}:${m.ids.join(",")}:${m.x}:${m.y}`} entries={entries} onClose={closeContextMenu} />
    </div>
  );
}
